import React from "react";
import { Link } from "react-router-dom";

import { LEGAL_CONFIG, PUBLIC_RESULT_SOURCES } from "./legal-config";
import { LegalPage, LegalSection } from "./legal-page";

const PrivacyPolicy: React.FC = () => {
  return (
    <LegalPage
      title="Polityka prywatności"
      lead={`Jak serwis ${LEGAL_CONFIG.serviceName} zbiera, wykorzystuje i chroni dane osobowe zawodników, organizatorów i użytkowników konta.`}
    >
      <LegalSection id="administrator" number="1" title="Kto odpowiada za Twoje dane">
        <p>
          Administratorem danych osobowych przetwarzanych w serwisie{" "}
          {LEGAL_CONFIG.serviceName}, dostępnym pod adresami{" "}
          {LEGAL_CONFIG.serviceDomains.join(" i ")}, jest{" "}
          <strong>{LEGAL_CONFIG.administratorName}</strong>,{" "}
          {LEGAL_CONFIG.administratorAddress}. Serwis prowadzony jest niezarobkowo, przez
          osobę fizyczną nieprowadzącą działalności gospodarczej — korzystanie z niego
          jest bezpłatne i nie pobieramy żadnych opłat.
        </p>
        <p>
          We wszystkich sprawach dotyczących danych osobowych — w tym w celu skorzystania
          z praw opisanych w punkcie 8 — napisz na adres{" "}
          <strong>{LEGAL_CONFIG.contactEmail}</strong>. Nie wyznaczono inspektora ochrony
          danych; korespondencję obsługuje bezpośrednio administrator.
        </p>
        <p>
          Kluby i organizatorzy, którzy prowadzą w serwisie własne ligi, decydują o zapisach
          na swoje wydarzenia i o kontakcie z uczestnikami. W tym zakresie są odrębnymi
          administratorami danych, a serwis udostępnia im wyłącznie narzędzie.
        </p>
      </LegalSection>

      <LegalSection id="dane" number="2" title="Jakie dane przetwarzamy">
        <p>Dane trafiają do serwisu trzema drogami.</p>

        <h3>2.1. Dane, które podajesz sam</h3>
        <table className="legal-table">
          <thead>
            <tr>
              <th>Sytuacja</th>
              <th>Zakres danych</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Założenie konta i logowanie</td>
              <td>
                adres e-mail, hasło (przechowywane wyłącznie w postaci skrótu
                kryptograficznego), opcjonalna nazwa wyświetlana, rola w serwisie,
                powiązanie z klubem i z profilem zawodnika, data utworzenia konta
              </td>
            </tr>
            <tr>
              <td>Zapis na kolejkę ligi (także bez konta, przez link organizatora)</td>
              <td>
                imię i nazwisko, opcjonalnie adres e-mail i numer telefonu, deklarowany
                poziom gry, imię i nazwisko partnera w deblu, status zapisu
              </td>
            </tr>
            <tr>
              <td>Pula „szukam partnera”</td>
              <td>
                identyfikator zawodnika, treść notatki, którą sam wpisujesz, oraz
                zgłoszenia od innych zawodników
              </td>
            </tr>
            <tr>
              <td>Uzupełnienie profilu zawodnika</td>
              <td>nazwa wyświetlana, płeć (potrzebna do kategorii i klasyfikacji)</td>
            </tr>
            <tr>
              <td>Wpisywanie wyników przez link do arkusza</td>
              <td>wyniki setów i meczów przypisane do konkretnych zawodników</td>
            </tr>
          </tbody>
        </table>

        <h3>2.2. Wyniki turniejowe pozyskane ze źródeł publicznych</h3>
        <p>
          Ranking prowadzony w serwisie opiera się na wynikach meczów publikowanych przez
          organizatorów lig i turniejów. Pobieramy je z ogólnodostępnych stron:
        </p>
        <ul>
          {PUBLIC_RESULT_SOURCES.map((source) => (
            <li key={source.name}>
              <a href={source.url} target="_blank" rel="noreferrer noopener">
                {source.name}
              </a>
            </li>
          ))}
        </ul>
        <p>
          Z tych źródeł pochodzą: imię i nazwisko zawodnika, nazwa klubu lub drużyny, data
          i nazwa turnieju, kategoria, składy par oraz wyniki poszczególnych meczów. Na tej
          podstawie serwis wylicza rating ELO, historię startów i statystyki.
        </p>
        <p>
          Ta część danych nie pochodzi od Ciebie, dlatego niniejsza polityka pełni
          jednocześnie rolę informacji z art. 14 RODO. Ze względu na liczbę zawodników
          w bazie (kilka tysięcy osób) indywidualne powiadomienie każdej z nich wymagałoby
          niewspółmiernie dużego wysiłku, dlatego — zgodnie z art. 14 ust. 5 lit. b RODO —
          informacja jest udostępniana publicznie na tej stronie.
        </p>

        <h3>2.3. Dane techniczne</h3>
        <p>
          Serwery, na których działa serwis, prowadzą standardowe logi: adres IP, datę
          i godzinę zapytania, adres żądanego zasobu, kod odpowiedzi oraz informacje
          o przeglądarce. Logi służą utrzymaniu serwisu i wykrywaniu nadużyć. Nie
          używamy narzędzi analitycznych, reklamowych ani żadnych mechanizmów śledzenia
          — szczegóły w{" "}
          <Link to="/cookies">informacji o plikach cookie i pamięci przeglądarki</Link>.
        </p>
      </LegalSection>

      <LegalSection id="cele" number="3" title="Po co i na jakiej podstawie prawnej">
        <table className="legal-table">
          <thead>
            <tr>
              <th>Cel</th>
              <th>Podstawa prawna</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Prowadzenie konta, logowanie, obsługa zapisów na kolejki i turnieje,
                dobieranie par
              </td>
              <td>
                art. 6 ust. 1 lit. b RODO — wykonanie umowy o świadczenie usługi drogą
                elektroniczną, którą zawierasz korzystając z tych funkcji
              </td>
            </tr>
            <tr>
              <td>
                Prowadzenie i publikowanie rankingu ELO, wyników meczów, klasyfikacji
                sezonowych i historii startów
              </td>
              <td>
                art. 6 ust. 1 lit. f RODO — prawnie uzasadniony interes polegający na
                prowadzeniu jawnego, weryfikowalnego rankingu sportowego dla lokalnego
                środowiska badmintona, opartego na wynikach, które organizatorzy publikują
                publicznie
              </td>
            </tr>
            <tr>
              <td>
                Kontakt organizatora z osobą zapisaną na kolejkę (potwierdzenie, zmiana
                terminu, odwołanie)
              </td>
              <td>art. 6 ust. 1 lit. b oraz lit. f RODO</td>
            </tr>
            <tr>
              <td>
                Bezpieczeństwo serwisu, diagnostyka błędów, przeciwdziałanie nadużyciom
                i nieuprawnionemu dostępowi
              </td>
              <td>art. 6 ust. 1 lit. f RODO — ochrona serwisu i jego użytkowników</td>
            </tr>
            <tr>
              <td>Ustalenie, dochodzenie lub obrona przed roszczeniami</td>
              <td>art. 6 ust. 1 lit. f RODO</td>
            </tr>
          </tbody>
        </table>
        <p>
          Nie przetwarzamy danych w celach marketingowych i nie wysyłamy informacji
          handlowych. Nie sprzedajemy ani nie udostępniamy danych podmiotom
          reklamowym.
        </p>
      </LegalSection>

      <LegalSection id="jawnosc" number="4" title="Co jest widoczne publicznie">
        <p>
          Serwis z założenia jest jawny. Bez logowania każdy może zobaczyć: nazwę
          zawodnika, jego rating ELO i historię jego zmian, rozegrane mecze wraz
          z wynikami, partnerów i przeciwników, przynależność do klubu lub ligi,
          klasyfikacje sezonowe oraz listy zapisów na kolejki.
        </p>
        <p>
          <strong>Nie są publikowane</strong>: adres e-mail, numer telefonu, hasło,
          adres IP ani żadne dane kontaktowe. Dane kontaktowe podane przy zapisie widzi
          wyłącznie organizator danej ligi oraz administrator serwisu.
        </p>
      </LegalSection>

      <LegalSection id="odbiorcy" number="5" title="Komu udostępniamy dane">
        <ul>
          <li>
            <strong>Dostawcy hostingu</strong> — {LEGAL_CONFIG.hostingProvider}. Serwery
            przechowujące bazę danych i pliki serwisu znajdują się na terenie Unii
            Europejskiej.
          </li>
          <li>
            <strong>Organizatorzy klubów i lig</strong> — w zakresie zapisów na
            prowadzone przez nich wydarzenia (imię i nazwisko, status zapisu, wyniki).
            Dane kontaktowe widzą tylko do momentu rozliczenia kolejki, o czym mowa
            w punkcie 6.
          </li>
          <li>
            <strong>Uprawnione organy</strong> — jeżeli obowiązek udostępnienia wynika
            z przepisów prawa.
          </li>
        </ul>
        <p>
          Dane nie są przekazywane poza Europejski Obszar Gospodarczy ani do organizacji
          międzynarodowych. Serwis nie ładuje żadnych zasobów z serwerów zewnętrznych —
          nawet kroje pism hostujemy u siebie — więc Twój adres IP nie trafia do
          żadnego podmiotu trzeciego.
        </p>
      </LegalSection>

      <LegalSection id="okres" number="6" title="Jak długo przechowujemy dane">
        <table className="legal-table">
          <thead>
            <tr>
              <th>Kategoria</th>
              <th>Okres</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Konto użytkownika</td>
              <td>
                do czasu usunięcia konta na Twoje żądanie; kopie zapasowe nadpisujemy
                w ciągu 30 dni
              </td>
            </tr>
            <tr>
              <td>Sesja logowania</td>
              <td>7 dni od zalogowania albo do wylogowania</td>
            </tr>
            <tr>
              <td>
                Dane kontaktowe podane przy zapisie na kolejkę oraz notatka w puli
                „szukam partnera”
              </td>
              <td>
                do rozliczenia tej kolejki — w chwili, gdy organizator ją zamyka i wyniki
                trafiają do rankingu, adres e-mail, numer telefonu i treść notatki są
                automatycznie kasowane; zapis i nazwisko zostają, kontakt znika
              </td>
            </tr>
            <tr>
              <td>Wyniki meczów, rating i historia startów</td>
              <td>
                bezterminowo — ranking jest historyczny i przeliczany od pierwszego
                zarejestrowanego meczu; usunięcie pojedynczego wyniku zmieniłoby rating
                wszystkich pozostałych uczestników tego meczu
              </td>
            </tr>
            <tr>
              <td>Logi serwera</td>
              <td>zgodnie z konfiguracją dostawcy hostingu, nie dłużej niż 12 miesięcy</td>
            </tr>
          </tbody>
        </table>
        <p>
          Kasowanie danych kontaktowych po kolejce jest automatyczne i nieodwracalne —
          dzieje się w tej samej operacji, w której wyniki wchodzą do rankingu, i obejmuje
          również wewnętrzny dziennik zdarzeń serwisu. Jedynym adresem e-mail, który
          zostaje na dłużej, jest ten przypisany do konta, bo służy do logowania; usuwasz
          go, usuwając konto.
        </p>
      </LegalSection>

      <LegalSection id="bezpieczenstwo" number="7" title="Jak chronimy dane">
        <ul>
          <li>Połączenie z serwisem jest szyfrowane (HTTPS).</li>
          <li>
            Hasła przechowujemy wyłącznie w postaci skrótu kryptograficznego — nikt,
            łącznie z administratorem, nie ma dostępu do hasła w jawnej postaci.
          </li>
          <li>
            Sesja logowania opiera się na tokenie o ograniczonej ważności (7 dni),
            zapisywanym w pamięci Twojej przeglądarki, nie w pliku cookie.
          </li>
          <li>
            Dostęp do panelu organizatora i do danych kontaktowych uczestników mają
            wyłącznie uwierzytelnione konta z odpowiednią rolą.
          </li>
          <li>Baza danych jest regularnie kopiowana na potrzeby odtworzenia po awarii.</li>
        </ul>
      </LegalSection>

      <LegalSection id="prawa" number="8" title="Twoje prawa">
        <p>W związku z przetwarzaniem danych przysługuje Ci prawo do:</p>
        <ul>
          <li>dostępu do danych i otrzymania ich kopii (art. 15 RODO),</li>
          <li>sprostowania danych nieprawidłowych lub niekompletnych (art. 16 RODO),</li>
          <li>usunięcia danych (art. 17 RODO),</li>
          <li>ograniczenia przetwarzania (art. 18 RODO),</li>
          <li>
            przenoszenia danych, które podałeś na podstawie umowy (art. 20 RODO),
          </li>
          <li>
            <strong>sprzeciwu wobec przetwarzania</strong> opartego na prawnie
            uzasadnionym interesie, w tym wobec publikowania Twojego nazwiska
            w rankingu (art. 21 RODO).
          </li>
        </ul>
        <p>
          Żądanie zgłoś na adres <strong>{LEGAL_CONFIG.contactEmail}</strong>. Odpowiadamy
          bez zbędnej zwłoki, najpóźniej w ciągu miesiąca.
        </p>
        <h3>Co dokładnie robimy po sprzeciwie wobec publikacji nazwiska</h3>
        <p>
          Dane kontaktowe i konto usuwamy na żądanie zawsze. Z samym wynikiem sportowym
          jest inaczej: wyniki meczów i turniejów są z natury jawne — publikuje je
          organizator rozgrywek, a zawodnik przystępując do ligi rozsądnie się tego
          spodziewa. Wynik dotyczy przy tym równocześnie Twoich partnerów
          i przeciwników, więc usunięcie go z historii zafałszowałoby ich rating.
          Dlatego co do zasady mamy tu ważne prawnie uzasadnione podstawy w rozumieniu
          art. 21 ust. 1 RODO i utrzymujemy publikację wyników.
        </p>
        <p>
          Każdy sprzeciw rozpatrujemy jednak indywidualnie i ważymy Twoją sytuację wobec
          naszego interesu. Jeżeli podasz szczególne okoliczności, które przeważają —
          zastępujemy Twoje imię i nazwisko w widokach publicznych oznaczeniem
          pseudonimowym (np. „Zawodnik #1234”), zachowując same mecze na potrzeby
          rankingu pozostałych zawodników. Odpowiedź wraz z uzasadnieniem otrzymasz
          niezależnie od rozstrzygnięcia.
        </p>
        <p>
          Masz również prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych
          Osobowych, ul. Stawki 2, 00-193 Warszawa.
        </p>
      </LegalSection>

      <LegalSection id="dobrowolnosc" number="9" title="Czy podanie danych jest obowiązkowe">
        <p>
          Podanie danych jest dobrowolne, ale niezbędne do korzystania z części funkcji.
          Bez adresu e-mail i hasła nie założysz konta; bez imienia i nazwiska organizator
          nie zapisze Cię na kolejkę; bez adresu e-mail lub telefonu organizator nie
          skontaktuje się z Tobą w sprawie zmiany terminu. Przeglądanie rankingów
          i wyników nie wymaga podawania żadnych danych ani zakładania konta.
        </p>
      </LegalSection>

      <LegalSection id="profilowanie" number="10" title="Automatyczne przetwarzanie i ranking">
        <p>
          Rating ELO oraz przydział do grup i par wyliczane są automatycznie na podstawie
          wyników meczów. Nie są to jednak decyzje wywołujące skutki prawne ani podobnie
          istotnie wpływające na Ciebie w rozumieniu art. 22 RODO — dotyczą wyłącznie
          klasyfikacji sportowej. Ostateczne decyzje o dopuszczeniu do rozgrywek
          podejmuje organizator, a nie algorytm.
        </p>
      </LegalSection>

      <LegalSection id="maloletni" number="11" title="Osoby niepełnoletnie">
        <p>
          Konto w serwisie mogą samodzielnie założyć osoby, które ukończyły 16 lat.
          Za osobę młodszą działa rodzic lub opiekun prawny. Wyniki zawodników
          niepełnoletnich pojawiają się w rankingu wyłącznie w takim zakresie, w jakim
          zostały opublikowane przez organizatora turnieju. Rodzic lub opiekun prawny może
          w każdej chwili zgłosić sprzeciw wobec publikacji danych dziecka na adres{" "}
          <strong>{LEGAL_CONFIG.contactEmail}</strong> — postępujemy wtedy tak, jak
          opisano w punkcie 8.
        </p>
      </LegalSection>

      <LegalSection id="zmiany" number="12" title="Zmiany polityki">
        <p>
          Politykę aktualizujemy, gdy zmienia się zakres przetwarzanych danych albo
          funkcje serwisu. Aktualna wersja jest zawsze dostępna pod tym adresem, a datę
          ostatniej zmiany podajemy na początku dokumentu. Zmiany istotne zapowiadamy
          w serwisie z wyprzedzeniem.
        </p>
      </LegalSection>
    </LegalPage>
  );
};

export default PrivacyPolicy;
